import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Divider,
    Grid,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
} from '@mui/material'
import {
    Person,
    Email,
    Phone,
    Lock,
    Visibility,
    VisibilityOff,
    Save,
} from '@mui/icons-material'
import { endUserApi } from '@api/endUser'
import { useAuthStore } from '@store/authStore'
import { useTranslation } from '@i18n'
import { toast } from 'react-toastify'

const EndUserSettings = () => {
    const { t, language } = useTranslation()
    const { user, setUser } = useAuthStore()
    const isRTL = language === 'ar'

    // Profile form state
    const [profileData, setProfileData] = useState({
        full_name: user?.full_name || '',
        phone: user?.phone || '',
    })

    // Password form state
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    })
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    })

    // Profile update mutation
    const updateProfileMutation = useMutation({
        mutationFn: (data) => endUserApi.updateProfile(data),
        onSuccess: () => {
            toast.success(isRTL ? 'تم تحديث الملف الشخصي بنجاح' : 'Profile updated successfully')
            // Update local user state
            setUser({ ...user, ...profileData })
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || (isRTL ? 'فشل تحديث الملف الشخصي' : 'Failed to update profile'))
        },
    })

    // Password change mutation
    const changePasswordMutation = useMutation({
        mutationFn: (data) => endUserApi.changePassword(data),
        onSuccess: () => {
            toast.success(isRTL ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully')
            setPasswordData({ current_password: '', new_password: '', confirm_password: '' })
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || (isRTL ? 'فشل تغيير كلمة المرور' : 'Failed to change password'))
        },
    })

    const handleProfileSubmit = (e) => {
        e.preventDefault()
        updateProfileMutation.mutate(profileData)
    }

    const handlePasswordSubmit = (e) => {
        e.preventDefault()

        if (passwordData.new_password !== passwordData.confirm_password) {
            toast.error(isRTL ? 'كلمات المرور غير متطابقة' : 'Passwords do not match')
            return
        }

        if (passwordData.new_password.length < 6) {
            toast.error(isRTL ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters')
            return
        }

        changePasswordMutation.mutate({
            current_password: passwordData.current_password,
            new_password: passwordData.new_password,
        })
    }

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }))
    }

    return (
        <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
                {isRTL ? 'الإعدادات' : 'Settings'}
            </Typography>

            <Grid container spacing={3}>
                {/* Profile Settings */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person color="primary" />
                            {isRTL ? 'المعلومات الشخصية' : 'Profile Information'}
                        </Typography>

                        <form onSubmit={handleProfileSubmit}>
                            <TextField
                                fullWidth
                                label={isRTL ? 'البريد الإلكتروني' : 'Email'}
                                value={user?.email || ''}
                                disabled
                                sx={{ mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <TextField
                                fullWidth
                                label={isRTL ? 'الاسم الكامل' : 'Full Name'}
                                value={profileData.full_name}
                                onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Person color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <TextField
                                fullWidth
                                label={isRTL ? 'رقم الهاتف' : 'Phone Number'}
                                value={profileData.phone}
                                onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                                sx={{ mb: 3 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Phone color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                startIcon={updateProfileMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                disabled={updateProfileMutation.isPending}
                            >
                                {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
                            </Button>
                        </form>
                    </Paper>
                </Grid>

                {/* Password Settings */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Lock color="primary" />
                            {isRTL ? 'تغيير كلمة المرور' : 'Change Password'}
                        </Typography>

                        <form onSubmit={handlePasswordSubmit}>
                            <TextField
                                fullWidth
                                type={showPasswords.current ? 'text' : 'password'}
                                label={isRTL ? 'كلمة المرور الحالية' : 'Current Password'}
                                value={passwordData.current_password}
                                onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => togglePasswordVisibility('current')} edge="end">
                                                {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <TextField
                                fullWidth
                                type={showPasswords.new ? 'text' : 'password'}
                                label={isRTL ? 'كلمة المرور الجديدة' : 'New Password'}
                                value={passwordData.new_password}
                                onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => togglePasswordVisibility('new')} edge="end">
                                                {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <TextField
                                fullWidth
                                type={showPasswords.confirm ? 'text' : 'password'}
                                label={isRTL ? 'تأكيد كلمة المرور' : 'Confirm Password'}
                                value={passwordData.confirm_password}
                                onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                                sx={{ mb: 3 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => togglePasswordVisibility('confirm')} edge="end">
                                                {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                color="warning"
                                fullWidth
                                startIcon={changePasswordMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <Lock />}
                                disabled={changePasswordMutation.isPending}
                            >
                                {isRTL ? 'تغيير كلمة المرور' : 'Change Password'}
                            </Button>
                        </form>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    )
}

export default EndUserSettings
